'use client'

import { useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'

/**
 * Aggiorna la pagina partita finché il referto non è stato letto.
 *
 * Il referto si legge **dopo la risposta** (`after(...)` nella pagina): alla
 * prima apertura di una gara conclusa la pagina è già stata resa senza i dati
 * del referto (formazioni dal referto, sestetti iniziali, andamento dei set) e,
 * quando l'estrazione finisce, nessuno lo dice al browser — i dati comparivano
 * solo ricaricando a mano.
 *
 * Qui si chiede un refresh a intervalli finché la pagina continua a dire che
 * l'estrazione è in corso: quando l'esito è definito la pagina non renderizza
 * più questo componente e il ciclo si spegne da solo. Il tetto di tentativi
 * copre il caso in cui l'estrazione muoia senza scrivere l'esito.
 */
export function RefertoAutoRefresh({
  intervalMs = 3000,
  maxAttempts = 20,
}: {
  intervalMs?: number
  /** ~1 minuto in tutto: l'estrazione deterministica dura pochi secondi. */
  maxAttempts?: number
}) {
  const router = useRouter()
  const attempts = useRef(0)

  useEffect(() => {
    const timer = window.setInterval(() => {
      attempts.current += 1
      if (attempts.current > maxAttempts) {
        window.clearInterval(timer)
        return
      }
      router.refresh()
    }, intervalMs)
    return () => window.clearInterval(timer)
  }, [router, intervalMs, maxAttempts])

  return null
}
